import React, { useState, useEffect } from 'react';
import { Table, message, Input } from 'antd';
import { getAllExploitations, deleteExploitation, getExploitationByClientId, getExploitationsByDomainManager, getExploitationsByManager } from '../../Utils/exploitations/exploitations';
import AnalysisBtns from '../../components/Exploitations/AnalysisBtns';
import InfoBtns from '../../components/Exploitations/InfoBtns';
import Loading from '../../components/Shared/Loading';
import Header from "../../components/Shared/Header";
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ActionsMenu from '../../components/Exploitations/ActionsMenu';
import { useSelector } from 'react-redux';
import { position } from '@chakra-ui/react';

const ExploitationsPage = () => {
  const [exploitations, setExploitations] = useState([]);
  const [filteredExploitations, setFilteredExploitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const navigation = useNavigate();
  const userRole = useSelector((state) => state.user.userRole.name);
  const userId = useSelector((state) => state.user.userInfo.user_id);


  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const filtered = exploitations?.filter(
      (exploitation) =>
        exploitation.company_name.toLowerCase().includes(searchText.toLowerCase()) ||
        exploitation.domain_name.toLowerCase().includes(searchText.toLowerCase()) ||
        exploitation.exploitation_name.toLowerCase().includes(searchText.toLowerCase()) ||
        exploitation.region.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredExploitations(filtered);
  }, [searchText, exploitations]);

  const fetchData = async () => {
    try {
      // const exploitationsResponse = await getAllExploitations();
      let exploitationsResponse;
      if (userRole === 'client') {
        exploitationsResponse = await getExploitationByClientId(userId);
      } else if (userRole === 'admin' || userRole === 'superadmin') {
        exploitationsResponse = await getAllExploitations();
      } else if (userRole === 'domain_manager') {
        exploitationsResponse = await getExploitationsByDomainManager(userId);
      } else if (userRole === 'exploitation_manager') {
        exploitationsResponse = await getExploitationsByManager(userId);
      } else {
        message.error('Rôle utilisateur non trouvé');
        return;
      }
      setExploitations(exploitationsResponse);
      setFilteredExploitations(exploitationsResponse);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      message.error('Échec de la récupération des données');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const columns = [
    {
      title: 'Nom Entreprise',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text) => {
        if (text === undefined || text === null || text === '' || text === 'undefined') {
          return 'N/A';
        }
        return text;
      }
    },
    {
      title: 'Nom Domain',
      dataIndex: 'domain_name',
      key: 'domain_name',
      render: (text) => {
        if (text === undefined || text === null || text === '' || text === 'undefined') {
          return 'N/A';
        }
        return text;
      }
    },
    {
      title: 'Nom Exploitation',
      dataIndex: 'exploitation_name',
      key: 'exploitation_name',
      render: (text) => {
        if (text === undefined || text === null || text === '' || text === 'undefined') {
          return 'N/A';
        }
        return text;
      }
    },
    {
      title: 'Région',
      dataIndex: 'region',
      key: 'region',
      render: (text) => {
        if (text === undefined || text === null || text === '' || text === 'undefined') {
          return 'N/A';
        }
        return text;
      }
    },
    {
      title: 'Surface',
      dataIndex: 'exploitiation_superficie_net',
      key: 'surface',
      render: (text) => (text ? `${text} Ha` : 'N/A')
    },
    {
      title: 'Date Création',
      dataIndex: 'creation_date',
      key: 'creation_date',
      render: (text) => {
        if (!text) return 'N/A';
        try {
          return new Date(text).toLocaleDateString('fr-FR');
        } catch (error) {
          console.error('Error formatting date:', error);
          return 'Invalid Date';
        }
      }
    },
    {
      title: 'Analyse',
      key: 'analyse',
      render: (_, record) => {
        if (!record || !record.exploitation_id || !record.supported_analysis) {
          return 'N/A';
        }
        return <AnalysisBtns exploitationId={record.exploitation_id} supportedAnalysis={record.supported_analysis} />;
      }
    },
    {
      title: 'Produit',
      dataIndex: 'product',
      key: 'product',
      render: (_, record) => {
        if (!record || !record.product) {
          return <span className="font-semibold text-center text-xs border-red-500 border p-1 bg-red-100 block rounded-md text-red-500">Aucun produit associé</span>;
        }
        let res = record.product.is_irrigation ? 'Irrigation' : '';
        if (record.product.is_soil) {
          res += res ? ' + Sol' : 'Sol';
        }
        return res || 'N/A';
      }
    },
    {
      title: 'Information',
      key: 'information',
      render: (_, record) => {
        if (record && record.product) {
          return <InfoBtns exploitationId={record.exploitation_id} product={record.product} />;
        }
        return null;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        if (!record || (userRole !== 'admin' && userRole !== 'superadmin')) {
          return null;
        }
        return (
          <ActionsMenu
            product={record.product}
            record={record}
            navigation={navigation}
            deleteExploitation={deleteExploitation}
            fetchData={fetchData}
          />
        );
      }
    }
  ];


  if (userRole !== 'admin' && userRole !== 'superadmin') {
    columns.splice(-1, 1);
    columns.splice(0, 1);
  }


  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {loading && <Loading />}
      <div className="bg-white h-full">
        <Header title="Liste des exploitations " />
        <div className="h-[calc(100%-100px)]">
          <div className="p-6">
            <Input
              placeholder="Rechercher paer nom entreprise, nom domaine, nom exploitation, région"
              prefix={<SearchOutlined />}
              onChange={handleSearch}
              style={{ marginBottom: 16 }}
            />
            <Table
              dataSource={filteredExploitations}
              columns={columns}
              rowKey="exploitation_id"
              pagination={{
                pageSize: 10,
                position: ['bottomCenter']
              }}
            />
            {(userRole === 'admin' || userRole === 'superadmin') &&
              <div
                className="fixed flex items-center justify-center rounded-full shadow-lg bg-blue-500 w-16 h-16 bottom-10 right-10 text-white text-2xl cursor-pointer hover:bg-blue-600 transition duration-300 ease-in-out"
                onClick={() => {
                  navigation('/admin/exploitation/create');
                }}
              >
                <PlusOutlined className="text-3xl" />
              </div>}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploitationsPage;